import { Fragment } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FriendListReferral } from "@/components/referrals/FriendList";
import { LeaderboardReferral } from "@/components/referrals/LeaderboardReferral";
import { usePrivy } from "@privy-io/react-auth";
import { useReferralList, useStats } from "@/lib/hooks/useApi";
import useInfiniteScroll from "@/lib/use-infinite-scroll";
import AnimatedLoading from "@/components/AnimatedLoading";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { generateTextShadow } from "@/lib/utils";
import { t } from "@/lib/i18n";

export const Route = createFileRoute("/(game)/referrals/")({
  component: ReferralsIndex,
});

function ReferralsIndex() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { data: statsData } = useStats(user?.wallet?.address ?? "");

  const {
    data: referralList,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
  } = useReferralList(statsData?.userId);

  const { lastElementRef } = useInfiniteScroll({
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  });

  // Extract friends array from infinite query result
  const friends = referralList?.pages?.flatMap((page) => page.friends) ?? [];

  if (isLoading) {
    return <AnimatedLoading />;
  }

  return (
    <Fragment>
      <div className="relative flex flex-col h-full bg-[#1D2A52] px-2">
        <FloatingLeftButton
          onClick={() => navigate({ to: "/home" })}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
        {/* Header */}
        <div className="w-full px-2 py-2 border-0 flex flex-col items-center mt-6">
          <span
            className="text-white font-black text-2xl text-center"
            style={{
              textShadow: generateTextShadow(1.5),
            }}
          >
            {t("referrals.title")}
          </span>
        </div>
        <Tabs
          defaultValue="my-referrals"
          className="w-full flex-1 min-h-0 !p-0"
        >
          <TabsList className="w-full">
            <TabsTrigger value="my-referrals">
              {t("referrals.tabs.myReferrals")}
            </TabsTrigger>
            <TabsTrigger value="leaderboard">
              {t("referrals.tabs.leaderboard")}
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="leaderboard"
            className="flex-1 min-h-0 flex flex-col p-0!"
          >
            <LeaderboardReferral />
          </TabsContent>
          <TabsContent
            value="my-referrals"
            className="flex-1 min-h-0 flex flex-col p-0!"
          >
            <FriendListReferral
              referral={friends}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              lastElementRef={lastElementRef}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Fragment>
  );
}
